import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LayoutGroup } from 'motion/react'
import {
  enviarCandidatura,
  fetchAuthProviders,
  fetchEmpresaPublica,
  fetchMe,
  fetchMinhasCandidaturas,
  fetchOAuthUrl,
  fetchVaga,
  fetchVagas,
  loginCandidato,
  registroCandidato,
} from './api'
import { HiBgScene } from '@/components/HiBgScene'
import { AuthModal } from './components/AuthModal'
import { EmpresasTree } from './components/EmpresasTree'
import { Footer } from './components/Footer'
import { Hero } from './components/Hero'
import { JobDetail } from './components/JobDetail'
import { JobList } from './components/JobList'
import { MinhasVagas } from './components/MinhasVagas'
import { SearchForm } from './components/SearchForm'
import { Topbar } from './components/Topbar'
import {
  EMPRESA_FALLBACK,
  FALLBACK_EMPRESAS,
  PROCESSO_PASSOS_DEFAULT,
} from './constants'
import {
  consumeAuthPurposeStorage,
  loadSession,
  persistSession,
  setAuthPurposeStorage,
} from './session'
import type {
  AuthProviders,
  AuthPurpose,
  AuthTab,
  Candidato,
  CandidaturaPublica,
  CarreirasView,
  EmpresaGrupo,
  EmpresaPublica,
  HashRoute,
  ModalStep,
  VagaDetalhe,
  VagaResumo,
} from './types'
import {
  brandFromVaga,
  empresasPrincipais,
  normalizeEmpresaGrupo,
  parseHash,
  setHash,
  sortEmpresas,
} from './utils'
import '../../styles/carreiras.css'

export function CarreirasPage() {
  const [empresa, setEmpresa] = useState<EmpresaPublica | null>(null)
  const [empresasGrupo, setEmpresasGrupo] = useState<EmpresaGrupo[]>(FALLBACK_EMPRESAS)
  const [empresaFiltro, setEmpresaFiltro] = useState('')
  const [lista, setLista] = useState<VagaResumo[]>([])
  const [total, setTotal] = useState(0)
  const [q, setQ] = useState('')
  const [onde, setOnde] = useState('')
  const [selecionada, setSelecionada] = useState<string | null>(null)
  const [detalhe, setDetalhe] = useState<VagaDetalhe | null>(null)
  const [carregandoLista, setCarregandoLista] = useState(false)
  const [carregandoDetalhe, setCarregandoDetalhe] = useState(false)
  const [view, setView] = useState<CarreirasView>('empregos')
  const [minhas, setMinhas] = useState<CandidaturaPublica[]>([])
  const [carregandoMinhas, setCarregandoMinhas] = useState(false)
  const [modal, setModal] = useState(false)
  const [modalStep, setModalStep] = useState<ModalStep>('auth')
  const [authTab, setAuthTab] = useState<AuthTab>('entrar')
  const [authPurpose, setAuthPurpose] = useState<AuthPurpose>('candidatura')
  const [providers, setProviders] = useState<AuthProviders>({
    google: false,
    linkedin: false,
    email: true,
  })
  const [candidato, setCandidato] = useState<Candidato | null>(null)
  const [token, setToken] = useState('')
  const [enviando, setEnviando] = useState(false)
  const [msg, setMsg] = useState('')
  const [erro, setErro] = useState('')
  const [routeType, setRouteType] = useState('home')
  const [processoPassos, setProcessoPassos] = useState<string[]>(PROCESSO_PASSOS_DEFAULT)
  const [ready, setReady] = useState(false)
  const [logoInHeader, setLogoInHeader] = useState(false)

  const qRef = useRef(q)
  const ondeRef = useRef(onde)
  const empresaFiltroRef = useRef(empresaFiltro)
  const tokenRef = useRef(token)
  const candidatoRef = useRef(candidato)
  const detalheRef = useRef(detalhe)
  const authPurposeRef = useRef(authPurpose)

  useEffect(() => {
    qRef.current = q
  }, [q])
  useEffect(() => {
    ondeRef.current = onde
  }, [onde])
  useEffect(() => {
    empresaFiltroRef.current = empresaFiltro
  }, [empresaFiltro])
  useEffect(() => {
    tokenRef.current = token
  }, [token])
  useEffect(() => {
    candidatoRef.current = candidato
  }, [candidato])
  useEffect(() => {
    detalheRef.current = detalhe
  }, [detalhe])
  useEffect(() => {
    authPurposeRef.current = authPurpose
  }, [authPurpose])

  const loggedIn = Boolean(token && candidato)

  const empresasSorted = useMemo(() => sortEmpresas(empresasGrupo), [empresasGrupo])
  /** Chips / select da busca: só marcas principais (árvore permanece completa). */
  const empresasFiltro = useMemo(
    () => empresasPrincipais(empresasSorted, { incluirHolding: false }),
    [empresasSorted],
  )

  const saveSession = useCallback((nextToken: string, nextCandidato: Candidato | null) => {
    setToken(nextToken || '')
    setCandidato(nextCandidato)
    persistSession(nextToken || '', nextCandidato)
  }, [])

  const clearSession = useCallback(() => {
    saveSession('', null)
  }, [saveSession])

  const updateAuthPurpose = useCallback((purpose: AuthPurpose) => {
    setAuthPurpose(purpose)
    setAuthPurposeStorage(purpose)
  }, [])

  const brandFor = useCallback(
    (v: VagaResumo) => brandFromVaga(v, empresasSorted, empresaFiltroRef.current),
    [empresasSorted],
  )

  const findEmpresaNome = useCallback(
    (slug: string) => empresasSorted.find((e) => e.slug === slug)?.nome || null,
    [empresasSorted],
  )

  const scrollToVagas = useCallback(() => {
    requestAnimationFrame(() => {
      document.getElementById('conteudo-principal')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }, [])

  const irParaSobre = useCallback(({ pushHash = true }: { pushHash?: boolean } = {}) => {
    setView('sobre')
    setSelecionada(null)
    setDetalhe(null)
    setRouteType('sobre')
    if (pushHash && window.location.hash !== '#/sobre') {
      history.replaceState(null, '', '#/sobre')
    }
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    })
  }, [])

  const carregarLista = useCallback(async (overrides?: {
    q?: string
    onde?: string
    empresa?: string
  }) => {
    setCarregandoLista(true)
    try {
      const res = await fetchVagas({
        q: overrides?.q ?? qRef.current,
        onde: overrides?.onde ?? ondeRef.current,
        empresa: overrides?.empresa ?? empresaFiltroRef.current,
      })
      setLista(res.dados || [])
      setTotal(res.total ?? res.meta?.total ?? (res.dados || []).length)
    } catch {
      setLista([])
      setTotal(0)
    } finally {
      setCarregandoLista(false)
    }
  }, [])

  const abrirVaga = useCallback(async (id: string) => {
    setView('empregos')
    setSelecionada(id)
    setCarregandoDetalhe(true)
    setDetalhe(null)
    setRouteType('vaga')
    try {
      const res = await fetchVaga(id)
      const data = (res.dados || res) as VagaDetalhe
      setDetalhe(data)
      if (window.location.hash !== `#/vaga/${id}`) {
        history.replaceState(null, '', `#/vaga/${id}`)
      }
    } catch {
      setDetalhe(null)
      setSelecionada(null)
    } finally {
      setCarregandoDetalhe(false)
    }
  }, [])

  const carregarMinhas = useCallback(async () => {
    const t = tokenRef.current
    const c = candidatoRef.current
    if (!(t && c)) {
      setMinhas([])
      return
    }
    setCarregandoMinhas(true)
    try {
      const res = await fetchMinhasCandidaturas(t)
      setMinhas(res.dados || [])
      if (Array.isArray(res.processo_passos) && res.processo_passos.length) {
        setProcessoPassos(res.processo_passos)
      }
    } catch (e) {
      setMinhas([])
      if (!(tokenRef.current && candidatoRef.current)) {
        setErro((e as Error).message || 'Faça login para ver suas vagas.')
      }
      const status = (e as Error & { status?: number }).status
      if (status === 401) clearSession()
    } finally {
      setCarregandoMinhas(false)
    }
  }, [clearSession])

  const abrirAuthMinhas = useCallback(() => {
    setModal(true)
    setModalStep('auth')
    updateAuthPurpose('minhas')
    setAuthTab('entrar')
    setMsg('')
    setErro('Entre com sua conta de candidato para ver Minhas vagas.')
    setEnviando(false)
  }, [updateAuthPurpose])

  const irParaMinhas = useCallback(
    async ({ pushHash = true }: { pushHash?: boolean } = {}) => {
      if (!(tokenRef.current && candidatoRef.current)) {
        setView('minhas')
        setMinhas([])
        setRouteType('minhas')
        if (pushHash && window.location.hash !== '#/minhas') {
          history.replaceState(null, '', '#/minhas')
        }
        abrirAuthMinhas()
        return
      }
      setView('minhas')
      setSelecionada(null)
      setDetalhe(null)
      setRouteType('minhas')
      if (pushHash && window.location.hash !== '#/minhas') {
        history.replaceState(null, '', '#/minhas')
      }
      await carregarMinhas()
    },
    [abrirAuthMinhas, carregarMinhas],
  )

  const irParaEmpregos = useCallback(
    ({ clearHash = true }: { clearHash?: boolean } = {}) => {
      setView('empregos')
      setSelecionada(null)
      setDetalhe(null)
      setRouteType(empresaFiltroRef.current ? 'empresa' : 'home')
      if (clearHash) {
        const hash = empresaFiltroRef.current
          ? `#/empresa/${empresaFiltroRef.current}`
          : '#/'
        setHash(hash)
      }
    },
    [],
  )

  const filtrarEmpresa = useCallback(
    async (
      slug: string,
      { pushHash = true, scroll = true }: { pushHash?: boolean; scroll?: boolean } = {},
    ) => {
      const next = String(slug || '').trim().toLowerCase()
      setEmpresaFiltro(next)
      empresaFiltroRef.current = next
      setSelecionada(null)
      setDetalhe(null)
      setView('empregos')
      setRouteType(next ? 'empresa' : 'home')
      if (pushHash) {
        setHash(next ? `#/empresa/${next}` : '#/')
      }
      await carregarLista({ empresa: next })
      if (scroll) scrollToVagas()
    },
    [carregarLista, scrollToVagas],
  )

  const handleAuthOk = useCallback(
    async (payload: Extract<HashRoute, { type: 'auth-ok' }>) => {
      if (payload.erro) {
        setModal(true)
        setModalStep('auth')
        setErro(`Falha no login social: ${payload.erro}`)
        history.replaceState(null, '', window.location.pathname + window.location.search)
        return
      }
      if (!payload.token) {
        history.replaceState(null, '', window.location.pathname + window.location.search)
        return
      }
      saveSession(payload.token, { nome: '', email: '', telefone: '' })
      try {
        const me = await fetchMe(payload.token)
        saveSession(payload.token, me.candidato || me.dados || { nome: '', email: '' })
      } catch {
        clearSession()
        setModal(true)
        setModalStep('auth')
        setErro('Sessão inválida. Faça login novamente.')
        history.replaceState(null, '', window.location.pathname + window.location.search)
        return
      }
      const purpose = consumeAuthPurposeStorage(authPurposeRef.current)
      setAuthPurpose('candidatura')
      history.replaceState(null, '', window.location.pathname + window.location.search)
      if (payload.vaga) {
        await abrirVaga(payload.vaga)
        setModal(true)
        setModalStep('cv')
        updateAuthPurpose('candidatura')
        setMsg('')
        setErro('')
      } else if (purpose === 'minhas') {
        setModal(false)
        await irParaMinhas({ pushHash: true })
      }
    },
    [abrirVaga, clearSession, irParaMinhas, saveSession, updateAuthPurpose],
  )

  // Bootstrap
  useEffect(() => {
    let cancelled = false

    async function init() {
      document.body.classList.add('carreiras-body')
      const session = loadSession()
      if (!cancelled) {
        setToken(session.token)
        setCandidato(session.candidato)
        tokenRef.current = session.token
        candidatoRef.current = session.candidato
      }

      try {
        const [empRes, provRes] = await Promise.all([
          fetchEmpresaPublica().catch(() => null),
          fetchAuthProviders().catch(() => null),
        ])
        if (cancelled) return

        if (empRes) {
          const dados = (empRes.dados || empRes) as EmpresaPublica
          setEmpresa(dados)
          const rawGrupo = dados.empresas_grupo
          if (Array.isArray(rawGrupo) && rawGrupo.length) {
            setEmpresasGrupo(
              rawGrupo.map(normalizeEmpresaGrupo).filter(Boolean) as EmpresaGrupo[],
            )
          } else {
            setEmpresasGrupo(FALLBACK_EMPRESAS.map((e) => ({ ...e })))
          }
        } else {
          setEmpresa(EMPRESA_FALLBACK)
          setEmpresasGrupo(FALLBACK_EMPRESAS.map((e) => ({ ...e })))
        }

        if (provRes?.dados) {
          setProviders((p) => ({ ...p, ...provRes.dados }))
        }

        const route = parseHash()
        if (route.type === 'empresa') {
          setEmpresaFiltro(route.slug)
          empresaFiltroRef.current = route.slug
        }

        await carregarLista(
          route.type === 'empresa' ? { empresa: route.slug } : undefined,
        )

        if (session.token && session.candidato) {
          try {
            const me = await fetchMe(session.token)
            if (!cancelled) {
              saveSession(session.token, me.candidato || me.dados || session.candidato)
            }
          } catch {
            if (!cancelled) clearSession()
          }
        }

        if (cancelled) return

        if (route.type === 'auth-ok') {
          await handleAuthOk(route)
        } else if (route.type === 'vaga') {
          await abrirVaga(route.id)
        } else if (route.type === 'minhas') {
          await irParaMinhas({ pushHash: false })
        } else if (route.type === 'sobre') {
          irParaSobre({ pushHash: false })
        } else if (route.type === 'empresa') {
          setRouteType('empresa')
          scrollToVagas()
        } else {
          setRouteType('home')
        }
      } finally {
        if (!cancelled) setReady(true)
      }
    }

    void init()

    return () => {
      cancelled = true
      document.body.classList.remove('carreiras-body')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- bootstrap once
  }, [])

  // Logo única: no navy no topo → migra para o header ao rolar
  useEffect(() => {
    const syncLogo = () => {
      const needsHeader = view === 'sobre' || view === 'minhas' || window.scrollY > 88
      setLogoInHeader(needsHeader)
    }
    syncLogo()
    window.addEventListener('scroll', syncLogo, { passive: true })
    return () => window.removeEventListener('scroll', syncLogo)
  }, [view])

  // Hash routing
  useEffect(() => {
    if (!ready) return

    const onHash = () => {
      const r = parseHash()
      if (r.type === 'auth-ok') void handleAuthOk(r)
      else if (r.type === 'vaga') void abrirVaga(r.id)
      else if (r.type === 'minhas') void irParaMinhas({ pushHash: false })
      else if (r.type === 'sobre') {
        irParaSobre({ pushHash: false })
      } else if (r.type === 'empresa') {
        void filtrarEmpresa(r.slug, { pushHash: false, scroll: true })
      } else {
        setEmpresaFiltro('')
        empresaFiltroRef.current = ''
        irParaEmpregos({ clearHash: false })
        void carregarLista({ empresa: '' })
      }
    }

    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [
    ready,
    abrirVaga,
    carregarLista,
    filtrarEmpresa,
    handleAuthOk,
    irParaEmpregos,
    irParaMinhas,
    irParaSobre,
  ])

  const onSearchSubmit = () => {
    setSelecionada(null)
    setDetalhe(null)
    setView('empregos')
    const hash = empresaFiltro ? `#/empresa/${empresaFiltro}` : '#/'
    setHash(hash)
    void carregarLista()
  }

  const onEmpresaChange = (slug: string) => {
    void filtrarEmpresa(slug, { pushHash: true, scroll: false })
  }

  const abrirCandidatura = () => {
    if (!detalhe?.id) return
    setModal(true)
    setMsg('')
    setErro('')
    setEnviando(false)
    updateAuthPurpose('candidatura')
    setModalStep(loggedIn ? 'cv' : 'auth')
    setAuthTab('entrar')
  }

  const fecharCandidatura = () => {
    setModal(false)
    setMsg('')
    setErro('')
    updateAuthPurpose('candidatura')
    if (modalStep === 'sucesso') setModalStep('cv')
  }

  const sair = () => {
    clearSession()
    setModal(false)
    setMinhas([])
    updateAuthPurpose('candidatura')
    irParaEmpregos({ clearHash: true })
  }

  const onAuthSubmit = async (data: { nome: string; email: string; senha: string }) => {
    if (enviando) return
    setEnviando(true)
    setErro('')
    setMsg('')
    try {
      const res =
        authTab === 'criar'
          ? await registroCandidato(data.nome, data.email, data.senha)
          : await loginCandidato(data.email, data.senha)
      saveSession(res.access || '', res.candidato || null)
      setEnviando(false)
      const purpose = consumeAuthPurposeStorage(authPurpose)
      setAuthPurpose('candidatura')
      if (purpose === 'minhas') {
        setModal(false)
        await irParaMinhas({ pushHash: true })
      } else {
        setModalStep('cv')
        setMsg('Login realizado. Envie seu currículo.')
      }
    } catch (err) {
      setErro((err as Error).message || 'Não foi possível autenticar.')
      setEnviando(false)
    }
  }

  const onOAuth = async (provider: 'google' | 'linkedin') => {
    const forMinhas = authPurpose === 'minhas'
    if (!forMinhas && !detalhe?.id) return
    setErro('')
    setEnviando(true)
    try {
      const res = await fetchOAuthUrl(provider, detalhe?.id)
      if (!res.authorize_url) throw new Error('URL de autorização indisponível.')
      window.location.href = res.authorize_url
    } catch (err) {
      setErro((err as Error).message || 'Não foi possível iniciar o login.')
      setEnviando(false)
    }
  }

  const onCandSubmit = async (fd: FormData) => {
    if (!detalhe?.id || enviando) return
    if (!loggedIn) {
      setModalStep('auth')
      setErro('Faça login para enviar a candidatura.')
      return
    }
    setEnviando(true)
    setErro('')
    setMsg('')
    try {
      const res = await enviarCandidatura(detalhe.id, fd, token)
      setMsg(res.mensagem || 'Candidatura enviada com sucesso!')
      setEnviando(false)
      setModalStep('sucesso')
    } catch (err) {
      setErro((err as Error).message || 'Não foi possível enviar.')
      setEnviando(false)
      const status = (err as Error & { status?: number }).status
      if (status === 401 || !loggedIn) {
        clearSession()
        setModalStep('auth')
      }
    }
  }

  const voltarLista = () => {
    setSelecionada(null)
    setDetalhe(null)
    setView('empregos')
    const hash = empresaFiltro ? `#/empresa/${empresaFiltro}` : '#/'
    setHash(hash)
    document.getElementById('cr-lista')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const empresaSobre =
    (detalhe && brandFor(detalhe).slug
      ? empresasSorted.find((e) => e.slug === brandFor(detalhe).slug)?.sobre
      : '') ||
    empresa?.apresentacao ||
    ''

  const detailBrand = detalhe ? brandFor(detalhe) : null

  return (
    <div className="cr-app relative w-full min-h-[115vh] overflow-x-hidden flex flex-col items-center font-sans selection:bg-white/20 selection:text-white">
      <HiBgScene />

      <a className="cr-skip relative z-20" href="#conteudo-principal">
        Ir para o conteúdo
      </a>

      <LayoutGroup id="cr-brand">
      <div className="relative z-10 w-full flex flex-col items-center flex-1">
        <Topbar
          view={view}
          routeType={routeType}
          candidato={candidato}
          loggedIn={loggedIn}
          logoInHeader={logoInHeader}
          onSair={sair}
          onNavEmpregos={() => {
            setEmpresaFiltro('')
            empresaFiltroRef.current = ''
            irParaEmpregos({ clearHash: true })
            void carregarLista({ empresa: '' })
          }}
          onNavSobre={() => irParaSobre({ pushHash: true })}
          onNavMinhas={() => void irParaMinhas({ pushHash: true })}
        />

        {view !== 'sobre' && (
          <Hero
            empresa={empresa}
            view={view}
            showLogo={!logoInHeader}
            routeType={routeType}
            candidato={candidato}
            loggedIn={loggedIn}
            onSair={sair}
            onNavEmpregos={() => {
              setEmpresaFiltro('')
              empresaFiltroRef.current = ''
              irParaEmpregos({ clearHash: true })
              void carregarLista({ empresa: '' })
            }}
            onNavSobre={() => irParaSobre({ pushHash: true })}
            onNavMinhas={() => void irParaMinhas({ pushHash: true })}
          />
        )}

        {view === 'sobre' ? (
          <main id="conteudo-principal" className="cr-main cr-main--sobre w-full">
            <section id="cr-empresas" className="cr-empresas" aria-label="Árvore do Grupo HI">
              <div className="cr-wrap">
                <div className="cr-empresas-tree" role="tree" aria-label="Marcas do Grupo HI">
                  {empresasSorted.length ? (
                    <EmpresasTree
                      empresas={empresasSorted}
                      onSelectEmpresa={(slug) => void filtrarEmpresa(slug)}
                    />
                  ) : (
                    <div className="cr-loading">Carregando árvore de marcas…</div>
                  )}
                </div>
              </div>
            </section>
          </main>
        ) : (
          <main id="conteudo-principal" className="cr-main w-full">
            <div className="cr-wrap">
              {view === 'empregos' ? (
                <div>
                  <SearchForm
                    q={q}
                    onde={onde}
                    empresaFiltro={empresaFiltro}
                    total={total}
                    empresasFiltro={empresasFiltro}
                    onQChange={setQ}
                    onOndeChange={setOnde}
                    onEmpresaChange={onEmpresaChange}
                    onSubmit={onSearchSubmit}
                    findEmpresaNome={findEmpresaNome}
                  />
                  <div className="cr-grid">
                    <section className="cr-card cr-list-panel" aria-label="Lista de vagas">
                      <div className="cr-panel-label">Vagas</div>
                      <div id="cr-lista">
                        <JobList
                          lista={lista}
                          selecionada={selecionada}
                          carregando={carregandoLista}
                          brandFor={brandFor}
                          onSelect={(id) => void abrirVaga(id)}
                        />
                      </div>
                    </section>
                    <aside className="cr-detail-panel" aria-live="polite">
                      <JobDetail
                        detalhe={detalhe}
                        carregando={carregandoDetalhe}
                        brand={detailBrand}
                        empresaSobre={empresaSobre}
                        empresa={empresa}
                        onCandidatar={abrirCandidatura}
                        onVoltar={voltarLista}
                      />
                    </aside>
                  </div>
                </div>
              ) : (
                <MinhasVagas
                  loggedIn={loggedIn}
                  carregando={carregandoMinhas}
                  minhas={minhas}
                  processoPassos={processoPassos}
                  brandFor={brandFor}
                  onLogin={abrirAuthMinhas}
                  onVerEmpregos={() => {
                    irParaEmpregos({ clearHash: true })
                    void carregarLista()
                  }}
                  onVerVaga={(id) => void abrirVaga(id)}
                />
              )}
            </div>
          </main>
        )}

        <Footer
          onSobre={() => irParaSobre({ pushHash: true })}
          onEmpregos={() => {
            irParaEmpregos({ clearHash: true })
            void carregarLista()
            scrollToVagas()
          }}
        />
      </div>
      </LayoutGroup>

      <AuthModal
        open={modal}
        step={modalStep}
        authTab={authTab}
        authPurpose={authPurpose}
        providers={providers}
        candidato={candidato}
        tituloVaga={detalhe?.titulo || 'Vaga'}
        msg={msg}
        erro={erro}
        enviando={enviando}
        processoPassos={processoPassos}
        onClose={fecharCandidatura}
        onSetAuthTab={(tab) => {
          setAuthTab(tab)
          setErro('')
          setMsg('')
        }}
        onOAuth={(p) => void onOAuth(p)}
        onAuthSubmit={(d) => void onAuthSubmit(d)}
        onCandSubmit={(fd) => void onCandSubmit(fd)}
        onIrMinhas={() => {
          setModal(false)
          setMsg('')
          setErro('')
          updateAuthPurpose('candidatura')
          void irParaMinhas({ pushHash: true }).then(() => {
            document.getElementById('conteudo-principal')?.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
            })
          })
        }}
      />
    </div>
  )
}

export default CarreirasPage
